import { GET_PSYCHOLOGISTS } from '../../queries/User';
import { Instance, flow, getParent, types } from 'mobx-state-tree';
import { PsychologistModel } from './PsychologistModel';
import { captureException } from '../../../utils/sentry/sentry';
import { clientFunction } from '../../../config/apollo/client';

export const PsychologistStore = types
  .model('PsychologistStore', {
    psychologists: types.optional(types.array(PsychologistModel), []),
    matchingId: types.maybeNull(types.string),
    dateUpdated: types.maybeNull(types.number),
    isFound: types.maybeNull(types.boolean),
    isManualMatchingSearchActive:types.maybeNull(types.boolean),
    isManual: types.maybeNull(types.boolean),
    state: types.maybe(types.enumeration(['pending', 'done', 'error']))
  })
  .actions(self => {
    const fetchPsychologists= flow(function* () {
      self.state = 'pending';
      try {
        const { data, error }: any = yield clientFunction((getParent(self) as any).language.name.key).query({ query: GET_PSYCHOLOGISTS, fetchPolicy: 'no-cache' });
        if (data) {
          const contacts = data.getMyProfile?.contacts;
          if(contacts && contacts.length>0) {
            contacts.map((c:any) => loadPsychologist({...c, inContact:true}));
          }
          const psychologists = data.getMyProfile?.psychologists;
          if(psychologists && psychologists.length>0) {
            psychologists.map((p:any) => {
              const inContact = contacts && contacts.length>0 && contacts.some((c:any) => c._id === p._id);
              if(!inContact) {
                loadPsychologist({...p, inContact: false});
              }
            });
          }

          if(data.getMyProfile?.matching) {
            addMatchingInfo(data.getMyProfile?.matching);
          }
        }
        self.state = 'done';
      } catch (error) {
        self.state = 'error';
        captureException(error);
        return null;
      }
    });

    const addMatchingInfo = (matchingInfo : any) => {
      self.dateUpdated = Number(matchingInfo?.dateUpdated);
      self.isFound = matchingInfo?.isFound;
      self.isManualMatchingSearchActive=matchingInfo?.isManualMatchingSearchActive;
      self.matchingId = matchingInfo?._id;
      self.psychologists = self.psychologists.map(p => {
        return {...p, lastMatching: false};
      });
      matchingInfo.value.forEach((v:any, index: number) => {
        if(index === 0) {
          self.isManual = v.isManual;
        }
        const psychoIndex = self.psychologists.findIndex(p => p._id === v.psychologist);
        if(psychoIndex !== -1) {
          self.psychologists[psychoIndex] = {
            ...self.psychologists[psychoIndex],
            lastMatching: true,
            isManual:v.isManual,
            score: v.score,
            ranking: v.ranking,
            criteria: v.criteria
          };
        }
      });

    };
    const isInContact = (id: string) => {
      if (!self.psychologists || self.psychologists.length === 0) {
        return false;
      } else {
        return self.psychologists.some(c => c._id === id && c.inContact);
      }
    };
    const addToContact = (id: string) => {
      if (!self.psychologists || self.psychologists.length === 0) {
        return;
      } else {
        const index= self.psychologists.findIndex(c => c._id === id);
        self.psychologists[index]= {...self.psychologists[index], inContact: true};
      }
    };
    const loadPsychologist = (psychologist: any) => {
      if(self.psychologists && self.psychologists.length>0) {
        const index = self.psychologists.findIndex(p => p._id === psychologist._id);
        if(index === -1) {
          self.psychologists?.push(psychologist);
        } else {
          self.psychologists[index] = psychologist;
        }
      } else {
        self.psychologists = [psychologist] as any;
      }
    };
    const clear = () => {
      self.psychologists = [];
      self.isFound = null;
      self.dateUpdated = null;
      self.matchingId = null;
    };


    const notFound = () => {
      clear();
      self.isFound = false;
      self.isManual = true;
      self.isManualMatchingSearchActive= true;
    };
    return {
      fetchPsychologists,
      addMatchingInfo,
      isInContact,
      addToContact,
      loadPsychologist,
      clear,
      notFound
    };
  })

  .views(self => ({
    get getPsychologists() {
      return self.psychologists?.slice().sort((p1,p2) =>{
        return (p1.ranking && p2.ranking && p1.ranking>p2.ranking)? 1 : -1;});
    },
    getPsychologist(id: string) {
      const index = self.psychologists.findIndex(p => p._id === id);
      if(index === -1) {
        return null;
      } else {
        return self.psychologists[index];
      }
    },
    get getMatchings() {
      return self.psychologists?.filter(p => p.lastMatching);
    },
    get getContacts() {
      return self.psychologists?.filter(p => p.inContact);
    },
  }));

export type IPsychologistStore = Instance<typeof PsychologistStore>;
